<template>
  <div class="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-2rem)]">
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center space-x-4">
        <button class="text-gray-500 hover:text-gray-800" @click="goBack">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <h2 class="text-xl font-bold text-gray-800">问诊历史</h2>
      </div>
      <div class="bg-gray-100 p-1 rounded-lg flex text-sm font-medium">
        <button @click="changeTab('special')" class="px-3 py-1.5 rounded-md transition-all" :class="activeTab === 'special' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'">专科问诊</button>
        <button @click="changeTab('open')" class="px-3 py-1.5 rounded-md transition-all" :class="activeTab === 'open' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'">开放问诊</button>
      </div>
    </div>

    <div v-if="!patientId" class="py-10 text-center text-gray-500 text-sm">未获取到患者ID，请先完善用户信息。</div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-left text-sm text-gray-600">
        <thead class="bg-gray-50 text-gray-500 font-medium">
          <tr>
            <th v-for="col in tableColumns" :key="col.key" class="py-3 px-4" :class="col.align === 'center' ? 'text-center' : ''">{{ col.label }}</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-if="!loading && records.length === 0">
            <td :colspan="tableColumns.length" class="py-8 px-4 text-center text-gray-400 text-sm">暂无问诊记录</td>
          </tr>
          <tr v-for="item in records" :key="rowKey(item)" class="hover:bg-gray-50 transition">
            <td v-for="col in tableColumns" :key="col.key" class="py-4 px-4" :class="col.align === 'center' ? 'text-center' : ''">
              <template v-if="col.type === 'action'">
                <button class="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition" @click="openDetail(item)">查看</button>
              </template>
              <template v-else-if="col.type === 'truncate'">
                <div class="truncate max-w-[420px]" :title="getCell(item, col.key)">{{ getCell(item, col.key) }}</div>
              </template>
              <template v-else>{{ getCell(item, col.key) }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="patientId" class="flex justify-between items-center mt-6 text-sm text-gray-500">
      <span>第 {{ page }}/{{ totalPages }} 页 共 {{ total }} 条</span>
      <div class="flex space-x-2">
        <button class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50" :disabled="loading || page <= 1" @click="goToPrevPage">上一页</button>
        <button class="px-3 py-1 border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50" :disabled="loading || page >= totalPages || !hasMore" @click="goToNextPage">下一页</button>
      </div>
    </div>

    <div v-if="showDetailModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm" @click="closeDetailModal">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto" @click.stop>
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-gray-800">问诊详情</h3>
          <button class="text-gray-400 hover:text-gray-600" @click="closeDetailModal">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div v-if="detailLoading" class="py-8 text-center text-gray-500 text-sm">加载中...</div>
        <div v-else-if="detailError" class="py-4 text-red-500 text-sm">{{ detailError }}</div>
        <div v-else-if="detailData" class="space-y-6">
          <div class="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div><span class="text-gray-400">问诊ID：</span><span>{{ detailId }}</span></div>
            <div><span class="text-gray-400">问诊时间：</span><span>{{ detailTime }}</span></div>
          </div>

          <div v-if="isOpen" class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">主诉</h4>
            <div class="bg-gray-50 px-3 py-2 rounded text-gray-700 whitespace-pre-line text-sm">{{ detailData.complaint || '' }}</div>
          </div>
          <div v-else class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">初步诊断</h4>
            <div class="bg-gray-50 px-3 py-2 rounded text-gray-700 text-sm">{{ specialDiseaseName }}</div>
          </div>

          <div class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">问答记录</h4>
            <div v-if="detailAnswers.length" class="space-y-3 text-sm">
              <div v-for="(answer, index) in detailAnswers" :key="index" class="p-3 rounded-lg bg-gray-50">
                <div class="text-gray-500 text-xs mb-1">第 {{ index + 1 }} 题</div>
                <div class="text-gray-800 mb-1">{{ answer.question_text }}</div>
                <div class="text-gray-600">回复：{{ answer.reply_text }}</div>
              </div>
            </div>
            <div v-else class="text-gray-400 text-sm">暂无问答记录</div>
          </div>

          <div v-if="detailTongueImage" class="border-t border-gray-100 pt-4">
            <h4 class="font-semibold text-gray-800 mb-2 text-sm">舌苔图</h4>
            <a :href="detailTongueImage" target="_blank" rel="noreferrer" class="inline-block">
              <img :src="detailTongueImage" alt="舌苔图" class="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition" />
            </a>
          </div>

          <div class="flex justify-end mt-4">
            <button class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition" @click="closeDetailModal">关闭</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest, parsePagination } from '@/utils/request'

export default {
  name: 'PatientDiagnosisRecords',
  data() {
    return { activeTab: 'special', patientId: '', records: [], page: 1, totalPages: 1, total: 0, pageSize: 15, hasMore: false, loading: false, showDetailModal: false, detailLoading: false, detailError: '', detailData: null, detailRecord: null }
  },
  computed: {
    isOpen() { return this.activeTab === 'open' },
    tableColumns() {
      return this.isOpen
        ? [{ key: 'id', label: '问诊ID' }, { key: 'complaint', label: '主诉', type: 'truncate' }, { key: 'created_at', label: '问诊时间' }, { key: '__action', label: '操作', type: 'action', align: 'center' }]
        : [{ key: 'session_id', label: '问诊ID' }, { key: 'disease_name', label: '专科' }, { key: '__time', label: '问诊时间' }, { key: '__action', label: '操作', type: 'action', align: 'center' }]
    },
    detailId() { return this.detailRecord ? (this.isOpen ? (this.detailRecord.id || '') : (this.detailRecord.session_id || '')) : '' },
    detailTime() { return this.detailRecord ? (this.isOpen ? (this.detailRecord.created_at || '') : (this.detailRecord.completed_at || this.detailRecord.created_at || '')) : '' },
    detailAnswers() {
      const answers = this.detailData && Array.isArray(this.detailData.answers) ? this.detailData.answers : []
      if (this.isOpen) {
        return answers.map((a) => {
          return {
            question_text: a && a.question_content ? a.question_content : '',
            reply_text: a && a.reply_content ? a.reply_content : ''
          }
        })
      }
      return answers.map((a) => {
        const selected = a && Array.isArray(a.selected_option_texts) ? a.selected_option_texts : []
        return {
          question_text: a && a.question_text ? a.question_text : '',
          reply_text: selected.join('、')
        }
      })
    },
    detailTongueImage() {
      if (!this.detailData) return ''
      return this.detailData.tongue_image || ''
    },
    specialDiseaseName() {
      if (!this.detailData) return ''
      return this.detailData.disease && this.detailData.disease.name ? this.detailData.disease.name : '未明确'
    }
  },
  mounted() {
    this.patientId = this.getPatientId()
    if (this.patientId) this.fetchHistory(1)
  },
  methods: {
    rowKey(item) { return this.isOpen ? item.id : item.session_id },
    getCell(item, key) {
      if (!item) return ''
      if (key === '__time') return item.completed_at || item.created_at || ''
      const value = item[key]
      return value === null || value === undefined ? '' : String(value)
    },
    getPatientId() {
      try {
        const patient_id = localStorage.getItem('patient_id') || ''
        if (patient_id) return patient_id
      } catch (e) {
        return ''
      }
      try {
        const raw = localStorage.getItem('patient_info')
        const info = raw ? JSON.parse(raw) : null
        return info && info.id ? String(info.id) : ''
      } catch (e) {
        return ''
      }
    },
    goBack() { if (this.$router) this.$router.back() },
    changeTab(tab) {
      if (this.activeTab === tab || this.loading) return
      this.activeTab = tab
      this.records = []
      this.page = 1
      this.totalPages = 1
      this.total = 0
      this.hasMore = false
      this.fetchHistory(1)
    },
    async fetchHistory(targetPage = 1) {
      if (!this.patientId || this.loading) return
      this.loading = true
      try {
        const url = this.isOpen ? '/v1/patient/diagnosis/open/history' : '/v1/patient/diagnosis/special/history'
        const response = await httpRequest('get', url, { headers: { 'Client-Type': '1' }, query: { page: targetPage, patient_id: this.patientId } })
        const pagination = parsePagination(response, targetPage, 'data')
        this.records = pagination.list
        this.page = pagination.page
        this.hasMore = pagination.has_more
        const meta = response && response.data && response.data.meta ? response.data.meta : {}
        const p = meta.pagination || {}
        this.total = typeof p.total === 'number' ? p.total : this.records.length
        this.pageSize = typeof p.per_page === 'number' ? p.per_page : this.pageSize
        this.totalPages = typeof p.total_pages === 'number' ? p.total_pages : (this.hasMore ? this.page + 1 : this.page)
      } catch (error) {
        this.records = []
      } finally {
        this.loading = false
      }
    },
    goToPrevPage() { if (this.page > 1 && !this.loading) this.fetchHistory(this.page - 1) },
    goToNextPage() { if (!this.loading && this.page < this.totalPages && this.hasMore) this.fetchHistory(this.page + 1) },
    openDetail(record) {
      if (!record) return
      this.detailRecord = record
      this.showDetailModal = true
      this.detailLoading = true
      this.detailError = ''
      this.detailData = null
      if (this.isOpen) return this.fetchOpenDetail(record.id)
      return this.fetchSpecialDetail(record.session_id)
    },
    closeDetailModal() {
      if (this.detailLoading) return
      this.showDetailModal = false
      this.detailRecord = null
      this.detailData = null
      this.detailError = ''
    },
    async fetchOpenDetail(id) {
      try {
        const response = await httpRequest('get', `/v1/patient/diagnosis/open/session/${id}`, { headers: { 'Client-Type': '1' }, query: { patient_id: this.patientId } })
        this.detailData = response && response.data ? response.data : null
      } catch (error) {
        this.detailError = error && error.message ? error.message : '加载详情失败'
      } finally {
        this.detailLoading = false
      }
    },
    async fetchSpecialDetail(session_id) {
      try {
        const response = await httpRequest('get', `/v1/patient/diagnosis/special/session/${session_id}/detail`, { headers: { 'Client-Type': '1' }, query: { patient_id: this.patientId } })
        this.detailData = response && response.data ? response.data : null
      } catch (error) {
        this.detailError = error && error.message ? error.message : '加载详情失败'
      } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>
